<template>
  <div data-testid="oc-final-review-step" class="final-review-step step-inner-padded">
    <div class="card">
      <div class="card-body p-4">
        <div class="section-title">{{ $t('order_creation.final_review.title') }}</div>
        <p class="lead-page-muted">{{ $t('order_creation.final_review.subtitle') }}</p>
        <div class="confirmation-type-selector">
          <div
            data-testid="oc-final-review-confirm-now"
            class="type-option"
            :class="{ 'is-active': !order.external_confirmation }"
            @click="order.external_confirmation = false">
            <div>
              <IconUnlink class="type-icon" size="24" />
            </div>
            <span class="type-label">{{ $t('order_creation.final_review.confirm_now.title') }}</span>
            <small class="type-hint text-muted">{{ $t('order_creation.final_review.confirm_now.subtitle') }}</small>
          </div>
          <div
            data-testid="oc-final-review-confirm-external"
            class="type-option"
            :class="{ 'is-active': order.external_confirmation }"
            @click="order.external_confirmation = true">
            <div>
              <IconUnlink class="type-icon" size="24" />
            </div>
            <span class="type-label">{{ $t('order_creation.final_review.confirm_external.title') }}</span>
            <small class="type-hint text-muted">{{
              $t('order_creation.final_review.confirm_external.subtitle')
            }}</small>
          </div>
        </div>
        <hr class="section-divider" />
        <div>
          <div
            data-testid="oc-final-review-reference-toggle"
            class="section-header"
            @click="showReferenceComment = !showReferenceComment">
            <div class="d-flex align-items-center gap-2">
              <div class="section-title">{{ $t('order_creation.final_review.reference_comment.title') }}</div>
              <IconAlertCircle
                v-if="!showReferenceComment && (errors.provider_job_id?.length || errors.comment?.length)"
                :size="20"
                class="text-danger" />
            </div>
            <div>
              <IconSquareChevronDownFilled
                :size="24"
                class="toggle-icon"
                :class="{ 'is-open': showReferenceComment }"
                :color="showReferenceComment ? '#0000001A' : '#00000099'" />
            </div>
          </div>
          <transition name="section-expand">
            <div v-show="showReferenceComment" data-testid="oc-final-review-reference-body" class="section-body">
              <LeadInput
                v-model="order.provider_job_id"
                data-testid="oc-final-review-reference-id-input"
                :label="$t('order_creation.final_review.reference_comment.ref_number_label')"
                placeholder="12345678"
                class="mb-3"
                :errors="errors.provider_job_id">
                <template #hint>{{ $t('order_creation.final_review.reference_comment.ref_number_hint') }}</template>
              </LeadInput>
              <LeadInput
                v-model="order.comment"
                data-testid="oc-final-review-reference-comment-input"
                :label="$t('order_creation.final_review.reference_comment.comment_label')"
                placeholder="..."
                type="textarea"
                rows="4"
                :errors="errors.comment">
                <template #hint>{{ $t('order_creation.final_review.reference_comment.comment_hint') }}</template>
              </LeadInput>
            </div>
          </transition>
        </div>
        <hr class="section-divider" />
        <div data-testid="oc-final-review-basic-data" class="basic-data-section">
          <div class="mb-3 section-title">{{ $t('order_creation.final_review.basic_data') }}</div>
          <p class="mb-1 order-title">{{ order.title }}</p>
          <p class="lead-page-muted">
            {{ order.workload_min }} {{ $t('to') }} {{ order.workload_max }}%,
            {{
              order.workload_permanent
                ? $t('order_creation.details_step.permanent_employment')
                : $t('order_creation.details_step.temporary_employment')
            }}
          </p>

          <div class="company-info">
            <div
              v-if="companyLogoUrl"
              class="company-logo avatar"
              :style="`background-image: url('${companyLogoUrl}')`" />
            <div v-else class="company-logo placeholder avatar" />
            <p class="company-data">
              <span>{{ order.company?.data?.name || order.company_name }}</span>
              <span class="lead-page-muted">{{
                order.company?.data?.address_street || order.company_street || ''
              }}</span>
            </p>
          </div>
          <div class="order-actions">
            <a v-if="order.directlink" :href="order.directlink" target="_blank" class="btn btn-link p-1">
              <IconLink class="me-2 text-muted" size="18" />
              <p class="m-0">{{ $t('order_creation.final_review.online_link') }}</p>
            </a>
            <p v-if="order.directlink && featureOrderManagment?.self_service" class="actions-separator">/</p>
            <button v-if="featureOrderManagment?.self_service" class="btn btn-link p-1" @click="openPreviewModal">
              <IconEye class="me-2 text-muted" size="18" />
              <p class="m-0">{{ $t('order_creation.final_review.advertising_preview') }}</p>
            </button>
          </div>
          <div data-testid="oc-final-review-products" class="products-section">
            <div class="section-title mb-3">{{ $t('order_creation.final_review.products') }}</div>
            <div class="products-list">
              <div class="product-row" v-for="product in productsToSave" :key="product.id">
                <div class="product-info">
                  <div class="avatar me-2" :style="{ backgroundColor: getProductBackground(product.id) }">
                    <img :src="product.image" :alt="product.name" v-if="product.image" />
                  </div>
                  <p class="product-data">
                    <span class="product-title">{{ product.name }}</span>
                    <span class="product-subtitle text-muted">{{ itemCartDescription(product) }}</span>
                  </p>
                </div>
                <p class="product-value">
                  {{ calculateProductTotal(product, selectedProducts) }}
                </p>
              </div>
              <div class="product-row total-row">
                <p class="product-title">Total</p>
                <p class="product-value">{{ formattedTotal }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer">
        <LeadButton
          data-testid="oc-final-review-start-order-button"
          class="start-order"
          variant="primary"
          :loading="isFinalizing"
          :disabled="!allowNext"
          @click="finalizeOrder">
          {{ $t('order_creation.final_review.start_button') }}
          <IconArrowNarrowRight :size="16" :stroke-width="2.5" />
        </LeadButton>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { fetchImageMeta } from '@/api/company';
import { useOrderCreationStore } from '@/stores/orderCreation';
import {
  IconAlertCircle,
  IconArrowNarrowRight,
  IconSquareChevronDownFilled,
  IconEye,
  IconLink,
  IconUnlink,
} from '@tabler/icons-vue';
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import { calculateCartTotal, calculateProductTotal, getProductCartDescription } from '../product/cartCalculations';
import type { IProductToSave } from '@/types/product';
import { convertCurrency } from '@/api/product';
import { formatCurrency } from '@/locale/format';
import { useUserStore } from '@/stores/user';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import LeadButton from '@/components/generic/LeadButton.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import { request } from '@/api/client';
import { useRouter } from 'vue-router';
import { createPreviewModalOpenEvent } from '@/events/modalEvents';
import type { Dispatcher } from '@/events/dispatcher';
import { inject } from 'vue';

const router = useRouter();
const userStore = useUserStore();
const orderCreationStore = useOrderCreationStore();
const { featureOrderManagment } = storeToRefs(useCustomerFeaturesStore());
const { order, productsToSave, selectedProducts } = storeToRefs(orderCreationStore);
const dispatcher = inject<Dispatcher>('dispatcher');

const allowNext = defineModel<boolean>('allowNext');

const companyLogoUrl = ref<string | null>(null);
const formattedTotal = ref<string>('');
const isFinalizing = ref(false);
const showReferenceComment = ref(false);
const errors = ref<Record<string, string[]>>({});

onMounted(() => {
  calculateProductsTotal();
  if (order.value.company?.data?.logo_image_id) {
    fetchImageMeta(order.value.company.data.logo_image_id).then(({ data }) => {
      companyLogoUrl.value = data.proxy.url_thumbnail;
    });
  }
  allowNext.value = true;
});

function itemCartDescription(productToSave: IProductToSave) {
  return getProductCartDescription(productToSave, selectedProducts.value);
}

function getProductBackground(productId: number) {
  const product = selectedProducts.value.find((p) => p.id === productId);
  return product?.image_background || '#F3F3F3';
}

async function calculateProductsTotal() {
  const totals = calculateCartTotal(productsToSave.value, selectedProducts.value);
  let totalWithCurrency = 0;
  const targetCurrency = userStore.currentCustomer?.currency || 'CHF';

  const conversions = Object.keys(totals)
    .filter((curr) => totals[curr] !== 0)
    .map((curr) => {
      if (curr === targetCurrency) {
        totalWithCurrency += totals[curr];
        return null;
      }

      return convertCurrency(totals[curr], curr, targetCurrency);
    })
    .filter((promise): promise is Promise<{ amount: number }> => promise !== null);

  // Handle both fulfilled and rejected promises without failing the entire calculation
  const results = await Promise.allSettled(conversions);

  results.forEach((result) => {
    if (result.status === 'fulfilled') totalWithCurrency += result.value.amount;
    else console.error('Currency conversion failed:', result.reason);
  });

  formattedTotal.value = formatCurrency(totalWithCurrency, targetCurrency);
}
function finalizeOrder() {
  const orderId = order.value.id;
  if (!orderId) {
    console.error('Order ID is missing, cannot finalize.');
    return;
  }
  const payload = {
    ...order.value,
    external_confirmation: order.value.external_confirmation ?? false,
  };
  errors.value = {};
  isFinalizing.value = true;
  request('POST', `/v1/account/order?control_state=1`, { control_state: 1 }, payload)
    .then(() => {
      isFinalizing.value = false;
      router.replace({ name: 'order.detail.info', params: { id: String(orderId) } });
    })
    .catch((err) => {
      isFinalizing.value = false;
      if (err.responseJson) {
        errors.value = err.responseJson.errors || {};
      } else {
        console.error('Unexpected error:', err);
      }
      console.error('Error finalizing order:', err);
    });
}

function openPreviewModal() {
  if (!order.value.id) {
    console.error('Order ID is null or undefined');
    return;
  }
  dispatcher?.dispatch(createPreviewModalOpenEvent(order.value.id));
}
</script>
<style scoped>
* {
  font-family: var(--font-brand);
}

.final-review-step {
  display: flex;
  justify-content: center;
}

.final-review-step > .card {
  width: 600px;
}

.confirmation-type-selector {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: auto auto 1fr;
  gap: 10px;
  margin: 20px 0;
}

.type-option {
  grid-row: span 3;
  display: grid;
  grid-template-rows: subgrid;
  row-gap: 10px;
  padding: 1rem;
  cursor: pointer;
  background-color: #fff;
  transition:
    background-color 0.2s,
    border-color 0.2s;
  border: 2px solid #eaecf0;
  border-radius: 8px;
}

.type-option > :first-child {
  align-self: start;
}
.type-option .type-label {
  align-self: end;
  font-size: 12px;
  font-weight: 600;
}
.type-option .type-hint {
  align-self: start;
  font-size: 11px;
  font-weight: 400;
}

.type-option:hover {
  border-color: #cfd2d6;
}

.type-option.is-active {
  border: 2px solid var(--tw-color-primary);
  box-shadow: 0px 4px 8px 0px #00000029;
  position: relative;
  z-index: 1;
}

.type-option.is-active .tabler-icon {
  stroke: var(--tw-color-primary);
}

.type-label {
  font-weight: 600;
  color: #495057;
}

.section-divider {
  margin: 30px -2rem;
}

.company-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 20px;
}

.order-title {
  font-size: 14px;
  font-weight: 400;
}

.basic-data-section .lead-page-muted {
  font-size: 14px !important;
}

.company-info .company-data {
  display: flex;
  flex-direction: column;
  margin: 0;
  font-size: 14px;
}

.order-actions {
  display: flex;
}

.order-actions .actions-separator {
  margin: 0 20px;
  color: #eaecf0;
  display: flex;
  align-items: center;
}

.order-actions .btn.btn-link {
  color: black;
  text-decoration: underline;
  font-weight: 600;
}

.products-section {
  margin-top: 30px;
}

.products-section .product-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.products-section .product-row.total-row {
  margin-top: -4px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.products-section .product-row .product-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.product-info .avatar {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.product-info .avatar img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.products-section .product-row .product-data {
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
}

.products-section .product-row .product-title {
  font-size: 13px;
  font-weight: 600;
  margin: 0;
}

.products-section .product-row .product-data .product-subtitle {
  font-size: 11px;
  font-weight: 500;
}

.products-section .product-value {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}

.card-footer {
  background-color: #f8f8f8;
  border-top: none;
  padding: 10.5px 10px;
}

.card-footer .start-order {
  display: flex;
  justify-self: flex-end;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #101828;
}

.section-toggle {
  width: 28px;
  height: 28px;
  background-color: #f2f4f7;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-icon {
  transition: transform 0.2s ease;
}

.toggle-icon.is-open {
  transform: rotate(180deg);
}

.section-body {
  margin-top: 16px;
}

.section-expand-enter-active,
.section-expand-leave-active {
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease;
  overflow: hidden;
}

.section-expand-enter-from,
.section-expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.section-expand-enter-to,
.section-expand-leave-from {
  max-height: 500px;
  opacity: 1;
}
</style>
